import React from 'react';
import { cn } from '@/lib/utils';

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  status?: 'pending' | 'streaming' | 'done';
}

interface Props {
  messages: Message[];
}

/**
 * Three-dot typing indicator used while the assistant is thinking.
 * Dots bounce with staggered delays so they form a rolling "..." rhythm.
 */
const TypingDots: React.FC = () => (
  <span className="inline-flex items-center gap-1 py-1" aria-label="digitando">
    <span
      className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70 animate-bounce"
      style={{ animationDelay: '0ms' }}
    />
    <span
      className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70 animate-bounce"
      style={{ animationDelay: '150ms' }}
    />
    <span
      className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70 animate-bounce"
      style={{ animationDelay: '300ms' }}
    />
  </span>
);

export const ChatMessageList: React.FC<Props> = ({ messages }) => {
  return (
    <div className="flex-1 overflow-auto p-4 space-y-3">
      {messages.map((msg, idx) => {
        const isPending = msg.status === 'pending';
        return (
          <div
            key={idx}
            className={cn(
              'max-w-[90%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words',
              msg.role === 'user'
                ? 'ml-auto bg-primary/10 border border-primary/20'
                : 'mr-auto bg-muted/40 border border-border',
            )}
          >
            {isPending ? (
              <span className="inline-flex items-center gap-2">
                {msg.content && <span>{msg.content}</span>}
                <TypingDots />
              </span>
            ) : (
              msg.content
            )}
          </div>
        );
      })}
    </div>
  );
};
